import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { useDialog } from '@/common/dialog/DialogContext';
import { MainBox } from '@/common/layout/MainBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { McdInvStatusDefinitionsDialog } from '@/sc/entities/mcdInv/dashboard/McdInvStatusDefinitionsDialog';
import { McdInvSlaCartesianChart } from '@/sc/entities/mcdInv/dashboard/inv/McdInvSlaCartesianChart';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, Checkbox, ListItemText, MenuItem, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { McdAnalystStateSelector } from '../McdAnalystStateSelector';
import { McdInvPriorQtrPercentCompleteChart } from './McdInvPriorQtrPercentCompleteChart';
import { McdInvPrioritizationGrid } from './McdInvPrioritizationGrid';
import { McdInvProgressChart } from './McdInvProgressChart';
import { McdStlmntProgressChart } from './McdPymtProgressChart';

export const McdInvDashboard = () => {

  const rsrc = useRsrc();

  const { openDialog } = useDialog();
  const { navState, assign } = useNavState();
  const dashboardNavState = navState['mcdInvDashboardState'] ?? {};

  const [periods, setPeriods] = useState();
  const [selectedPeriod, setSelectedPeriod] = useState();

  const [selectedStates, setSelectedStates] = useState([]);
  const [openGroups, setOpenGroups] = useState({});

  const [ctrts, setCtrts] = useState();
  const [selectedCtrts, setSelectedCtrts] = useState([]);

  const [dueDates, setDueDates] = useState();
  const [selectedDueDate, setSelectedDueDate] = useState();

  const [mcdInvOrigins, setMcdInvOrigins] = useState();
  const [selectedMcdInvOrigins, setSelectedMcdInvOrigins] =  useState([]);

  const [exportReq, setExportReq] = useState();

  useEffect(() => {
    rsrc.data('sc', 'MCD_INV_PERIOD_FILTER_DATA_PLUGIN', {}).then(periods => {
      setPeriods(periods);
      // Restore nav state
      if (dashboardNavState.selectedPeriodId) {
        const period = periods.find(p => p.id === dashboardNavState.selectedPeriodId);
        if (period) setSelectedPeriod(period);
      }
      // or set to first period
      else if (periods?.length > 0) {
        const selectedPeriod = periods[0];
        setSelectedPeriod(selectedPeriod);
      }
    });

    rsrc.rows('md', 'CTRT', { expr: ExprUtils.whereParams('type', 'MCD'), sortModel: [{ colId: 'name', sort: 'asc' }] }).then(ctrts => {
      setCtrts(ctrts);
      // Restore nav state
      if (dashboardNavState.selectedCtrtCodes) {
        const matchedCtrts = ctrts.filter(ctrt =>
          dashboardNavState.selectedCtrtCodes.includes(ctrt.code)
        );
        if (matchedCtrts.length > 0) setSelectedCtrts(matchedCtrts);
      }
    });

    rsrc.data('sc', 'MCD_INV_ORIGIN_DATA_PLUGIN').then(mcdInvOrigins => {
      setMcdInvOrigins(mcdInvOrigins);
      // Restore nav state
      if (dashboardNavState.selectedMcdInvOrigins) {
        const origins = mcdInvOrigins.filter(o => dashboardNavState.selectedMcdInvOrigins.includes(o.id));
        if (origins.length > 0) setSelectedMcdInvOrigins(origins);
      } else {
        setSelectedMcdInvOrigins([mcdInvOrigins[0]]);
      }
    });

  }, []);

  useEffect(() => {
    if (!selectedPeriod?.id) {
      return;
    }
    rsrc.data('sc', 'MCD_INV_CHECK_RUN_DATE_FILTER_DATA_PLUGIN', { selectedPeriodId: selectedPeriod.id }).then(dates => {
      setDueDates(dates);
      // Restore nav state
      if (dashboardNavState.selectedDueDateEndDate) {
        const dueDate = dates.find(d => d.endDate === dashboardNavState.selectedDueDateEndDate);
        if (dueDate) {
          setSelectedDueDate(dueDate);
          return;
        }
      }

      // Otherwise use default selected date
      if (dates?.length > 0) {
        for (let date of dates) {
          if (date.selected) {
            setSelectedDueDate(date);
          }
        }
      }
    });
  }, [selectedPeriod]);

  const openDefinitionsDialog = () => {
    openDialog(McdInvStatusDefinitionsDialog)
  }
  
  useEffect(() => {
    const newDashboardNavState = {
      selectedPeriodId: selectedPeriod?.id,
      selectedStateCodes: selectedStates.map(s => s.code),
      selectedCtrtCodes: selectedCtrts.map(c => c.code),
      selectedDueDateEndDate: selectedDueDate?.endDate,
      openGroups
    };

    assign('mcdInvDashboardState', newDashboardNavState);
  }, [selectedPeriod, selectedStates, selectedCtrts, selectedDueDate, openGroups]);

  useEffect(() => {
    setExportReq({
      uiType: 'DASHBOARD',
      entity: 'MCD_INV',
      ctrtIds: selectedCtrts?.map(c => c.id),
      stateIds: selectedStates?.map(s => s.id),
      periodId: selectedPeriod?.id,
      startDate: selectedDueDate?.startDate,
      endDate: selectedDueDate?.endDate,
    });
  }, [selectedPeriod, selectedStates, selectedCtrts, selectedDueDate]);

  return (
    <MainBox>
      {/* Title */}
      <Box sx={{
        mb: 1, display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <Title icon={fas.faTasks} title="Medicaid Invoice Dashboard" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
          <TitleButton key="comparePolFormulaButton" icon={fas.faInfoCircle} toolTip="View Definitions" onClick={openDefinitionsDialog} />
          <ExportButton exportReq={exportReq} />
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        {/* Period */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Invoice Period"
          value={selectedPeriod?.id || ''}
          onChange={event => {
            const period = periods.find(p => p.id === event.target.value);
            setSelectedPeriod(period);
          }}>
          {periods?.map(period => (
            <MenuItem key={period.id} value={period.id}>
              {period.code}
            </MenuItem>
          ))}
        </TextField>

        {/* States */}
        <McdAnalystStateSelector
          onSelectionChange={setSelectedStates}
          onGroupsChange={setOpenGroups}
          selectedStateCodes={dashboardNavState?.selectedStateCodes}
          initOpenGroups={dashboardNavState?.openGroups}
        />
          

        {/* Contracts */}
        <TextField fullWidth select
          variant="outlined"
          label="Contracts"
          value={selectedCtrts}
          onChange={event => setSelectedCtrts(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((p) => p.code).join(', ')
          }}>
          {ctrts?.map(ctrt => (
            <MenuItem key={ctrt.code} value={ctrt}>
              <Checkbox checked={selectedCtrts?.some((p) => p.code === ctrt.code)} />
              <ListItemText primary={ctrt.name} />
            </MenuItem>
          ))}
        </TextField>

        {/* Check Run Dates */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Check Run Date"
          value={selectedDueDate || ''}
          onChange={event => setSelectedDueDate(event.target.value)}
        >

          {/* Deselect option */}
          <MenuItem value={null}>
            <em>None</em>
          </MenuItem>

          {dueDates?.map(dueDate => (
            <MenuItem key={dueDate.endDate} value={dueDate}>
              {dueDate.displayValue}
            </MenuItem>
          ))}
        </TextField>

        {/* Mcd Inv Origin */}
        <TextField fullWidth select
          variant="outlined"
          label="Origin"
          value={selectedMcdInvOrigins}
          onChange={event => setSelectedMcdInvOrigins(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((o) => o.displayName).join(', ')
          }}>
          {mcdInvOrigins?.map(mcdInvOrigin => (
            <MenuItem key={mcdInvOrigin.name} value={mcdInvOrigin}>
              <Checkbox checked={selectedMcdInvOrigins?.some((o) => o.name === mcdInvOrigin.name)} />
              <ListItemText primary={mcdInvOrigin.displayName} />
            </MenuItem>
          ))}
        </TextField>

      </Box>

      <SpacerLayout>
        <Spacer vertical>

          {/* Row 1 */}
          <Spacer weights={[1, 1, 1.75]}>
            <DashboardCard title="Invoice Progress" sx={{ flex: 1 }}>
              <McdInvProgressChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
                selectedMcdInvOrigins={selectedMcdInvOrigins}
              />
            </DashboardCard>

            <DashboardCard title="Settlement Progress" sx={{ flex: 1 }}>
              <McdStlmntProgressChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
              />
            </DashboardCard>

            <DashboardCard title="Prioritization Grid" sx={{ height: '100%' }}>
              <McdInvPrioritizationGrid
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
                selectedMcdInvOrigins={selectedMcdInvOrigins}
              />
            </DashboardCard>
          </Spacer>

          {/* Row 2 */}
          <Spacer weights={[1, 1, 1.75]}>
            <DashboardCard title="CLD Progress" sx={{ height: '100%' }}>
              <McdInvProgressChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
                selectedMcdInvOrigins={selectedMcdInvOrigins}
                isCld
              />
            </DashboardCard>

            <DashboardCard title="Prior Quarter % Completion" sx={{ height: '100%' }}>
              <McdInvPriorQtrPercentCompleteChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
              />
            </DashboardCard>

            <DashboardCard title="SLA Tracker" sx={{ height: '100%' }}>
              <McdInvSlaCartesianChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
              />
            </DashboardCard>
          </Spacer>

        </Spacer>
      </SpacerLayout>

    </MainBox >
  );
};